import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Send, Smartphone, Search, Loader2, Download, Megaphone, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { plural } from '../../utils/format';

// Settings → Send a notification. An admin writes a push and sends it to staff phones by hand:
// "please update the app", an office closure, a policy change. The automatic pushes (shift
// assigned, changed, reminders) carry on as before; this is for everything else.

const TEMPLATES = {
  app_update: {
    kind: 'app_update',
    title: 'Update Rostery',
    body: 'A new version of Rostery is ready. Please update it from the App Store or Google Play so you keep getting shift notifications.',
  },
  announcement: { kind: 'announcement', title: '', body: '' },
};
const TITLE_MAX = 65;
const BODY_MAX = 240;
const AUDIENCES = [
  { value: 'workers', label: 'Support workers' },
  { value: 'everyone', label: 'Everyone' },
  { value: 'selected', label: 'Choose people' },
];
const AUDIENCE_LABEL = { workers: 'Support workers', everyone: 'Everyone', selected: 'Chosen people' };
const when = (epoch) => {
  if (!epoch) return '';
  const d = new Date(epoch * 1000);
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function StaffBroadcast() {
  const confirm = useConfirm();
  const [people, setPeople] = useState(null);
  const [history, setHistory] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState(TEMPLATES.app_update);
  const [audience, setAudience] = useState('workers');
  const [picked, setPicked] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(() => {
    setLoadError(false);
    Promise.all([api.get('/notifications/broadcast/people'), api.get('/notifications/broadcasts')])
      .then(([p, h]) => { setPeople(p.data || []); setHistory(h.data || []); })
      .catch(() => setLoadError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const audienceList = useMemo(() => {
    const list = people || [];
    if (audience === 'workers') return list.filter((p) => p.worker);
    if (audience === 'selected') return list.filter((p) => picked.has(p.id));
    return list;
  }, [people, audience, picked]);
  const reach = {
    people: audienceList.length,
    phones: audienceList.reduce((n, p) => n + p.devices, 0),
    noPhone: audienceList.filter((p) => !p.devices).length,
  };
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (people || []).filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [people, query]);

  const togglePick = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const send = async () => {
    const errs = {};
    if (!form.title.trim()) errs.title = 'Add a title.';
    if (!form.body.trim()) errs.body = 'Write the message.';
    if (audience === 'selected' && !picked.size) errs.audience = 'Choose at least one person.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    if (!reach.phones) { toast.error("None of these people have the Rostery app set up on a phone yet, so there's nobody to send to."); return; }
    const ok = await confirm(
      `“${form.title.trim()}” goes to ${plural(reach.people, 'person', 'people')} on ${plural(reach.phones, 'phone')}.${reach.noPhone ? ` ${plural(reach.noPhone, 'person', 'people')} without the app on a phone won't get it.` : ''}`,
      { title: 'Send this notification?', confirmLabel: 'Send now' });
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await api.post('/notifications/broadcast', {
        title: form.title.trim(), body: form.body.trim(), kind: form.kind, audience,
        userIds: audience === 'selected' ? [...picked] : undefined,
      });
      toast.success(`Sent to ${plural(data.accepted, 'phone')}${data.failed ? ` · ${data.failed} couldn't be reached` : ''}`);
      load();
    } catch (e) {
      const d = e.response?.data || {};
      if (d.field) setErrors({ [d.field]: d.message });
      else toast.error(d.message || "The notification wasn't sent. Try again.");
    } finally { setSending(false); }
  };

  if (loadError) {
    return (
      <p className="text-sm text-slate-600 flex items-center gap-2">
        <AlertCircle size={15} className="text-rose-500" /> We couldn't load your team.
        <button type="button" onClick={load} className="font-semibold text-primary-700 hover:underline">Try again</button>
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600 max-w-2xl">
        Send a push notification to your team's phones: ask everyone to update the app, or share news from the office.
        Shift notifications (new, changed, cancelled, reminders) are still sent automatically.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
        {/* ── Compose ── */}
        <div className="space-y-5">
          <div>
            <p className="text-[13px] font-medium text-slate-700 mb-2">What kind of notification</p>
            <div role="radiogroup" aria-label="Notification type" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                ['app_update', Download, 'App update needed', 'Tapping it opens the App Store or Google Play.'],
                ['announcement', Megaphone, 'Announcement', 'Tapping it opens the Rostery app.'],
              ].map(([k, Icon, label, hint]) => (
                <button key={k} type="button" role="radio" aria-checked={form.kind === k}
                  onClick={() => { setForm(k === 'app_update' ? TEMPLATES.app_update : { ...TEMPLATES.announcement }); setErrors({}); }}
                  className={clsx('text-left rounded-xl border p-3 transition-colors flex gap-3',
                    form.kind === k ? 'border-primary-400 bg-primary-50/60 ring-1 ring-primary-200' : 'border-slate-200 bg-white hover:bg-slate-50')}>
                  <span className={clsx('w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                    form.kind === k ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600')}><Icon size={17} /></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-900">{label}</span>
                    <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label htmlFor="bc-title" className="text-[13px] font-medium text-slate-700">Title</label>
              <span className={clsx('text-xs tabular-nums', form.title.length > TITLE_MAX ? 'text-rose-600' : 'text-slate-500')}>{form.title.length}/{TITLE_MAX}</span>
            </div>
            <input id="bc-title" value={form.title} maxLength={TITLE_MAX} onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Office closed Monday" aria-invalid={!!errors.title}
              className={clsx('input-field mt-1.5', errors.title && '!border-rose-400')} />
            {errors.title && <p role="alert" className="text-xs text-rose-600 mt-1">{errors.title}</p>}
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label htmlFor="bc-body" className="text-[13px] font-medium text-slate-700">Message</label>
              <span className={clsx('text-xs tabular-nums', form.body.length > BODY_MAX ? 'text-rose-600' : 'text-slate-500')}>{form.body.length}/{BODY_MAX}</span>
            </div>
            <textarea id="bc-body" rows={3} value={form.body} maxLength={BODY_MAX} onChange={(e) => set('body', e.target.value)}
              placeholder="Keep it short: phones show the first two lines." aria-invalid={!!errors.body}
              className={clsx('input-field mt-1.5', errors.body && '!border-rose-400')} />
            {errors.body && <p role="alert" className="text-xs text-rose-600 mt-1">{errors.body}</p>}
          </div>

          <div>
            <p className="text-[13px] font-medium text-slate-700 mb-2">Who gets it</p>
            <div role="radiogroup" aria-label="Who gets it" className="inline-flex gap-0.5 bg-slate-100 rounded-lg p-0.5">
              {AUDIENCES.map((a) => (
                <button key={a.value} type="button" role="radio" aria-checked={audience === a.value}
                  onClick={() => { setAudience(a.value); setErrors((e) => ({ ...e, audience: undefined })); }}
                  className={clsx('h-8 px-3 rounded-md text-xs font-semibold transition-colors',
                    audience === a.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')}>
                  {a.label}
                </button>
              ))}
            </div>
            {errors.audience && <p role="alert" className="text-xs text-rose-600 mt-1">{errors.audience}</p>}

            {audience === 'selected' && (
              <div className="mt-3 border border-slate-200 rounded-xl overflow-hidden bg-white">
                <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people…" aria-label="Search people"
                      className="w-full h-9 pl-8 pr-3 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
                  </div>
                  <button type="button" onClick={() => setPicked(new Set([...picked, ...shown.map((p) => p.id)]))}
                    className="h-9 px-2.5 text-xs font-semibold text-primary-700 hover:bg-primary-50 rounded-lg shrink-0">Select all</button>
                  {picked.size > 0 && (
                    <button type="button" onClick={() => setPicked(new Set())}
                      className="h-9 px-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-lg shrink-0">Clear</button>
                  )}
                </div>
                <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                  {people === null && <li className="px-3 py-3 text-sm text-slate-500">Loading your team…</li>}
                  {people && !shown.length && <li className="px-3 py-3 text-sm text-slate-500">No one matches “{query}”.</li>}
                  {shown.map((p) => (
                    <li key={p.id}>
                      <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)} className="w-4 h-4 rounded border-slate-300 accent-primary-600" />
                        <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{p.name}</span>
                        <span className="text-xs text-slate-500 capitalize shrink-0">{String(p.role || '').replace(/_/g, ' ')}</span>
                        {p.devices
                          ? <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 shrink-0"><Smartphone size={12} />{p.devices}</span>
                          : <span className="text-[11px] text-slate-500 shrink-0">no app yet</span>}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Reach + send */}
          <div className="flex items-center justify-between gap-4 flex-wrap rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <p className="text-sm text-slate-700">
              {people === null ? 'Working out who this reaches…' : (
                <>Reaches <strong className="tabular-nums">{plural(reach.people, 'person', 'people')}</strong> on <strong className="tabular-nums">{plural(reach.phones, 'phone')}</strong>
                  {reach.noPhone > 0 && <span className="text-slate-500"> · {reach.noPhone} without the app on a phone won't get it</span>}</>
              )}
            </p>
            <button type="button" onClick={send} disabled={sending || people === null} className="btn-primary">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send notification
            </button>
          </div>
        </div>

        {/* ── Preview: how it looks on a phone ── */}
        <div className="lg:sticky lg:top-24">
          <p className="text-[13px] font-medium text-slate-700 mb-2">Preview</p>
          <div className="rounded-[28px] bg-gradient-to-b from-slate-800 to-slate-900 p-3 shadow-lg">
            <p className="text-center text-[11px] text-white/60 tabular-nums pt-1 pb-3">
              {new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}
            </p>
            <div className="rounded-2xl bg-white/90 backdrop-blur p-3 shadow">
              <div className="flex items-center gap-2">
                <img src="/apple-touch-icon.png" alt="" className="w-5 h-5 rounded-md" />
                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Rostery</span>
                <span className="ml-auto text-[11px] text-slate-500">now</span>
              </div>
              <p className="mt-1.5 text-[13px] font-semibold text-slate-900 break-words">{form.title.trim() || 'Title'}</p>
              <p className="text-[13px] text-slate-700 leading-snug break-words line-clamp-4">{form.body.trim() || 'Your message appears here.'}</p>
            </div>
            <p className="text-center text-[11px] text-white/60 pt-3 pb-1">
              {form.kind === 'app_update' ? 'Tapping opens the App Store / Google Play' : 'Tapping opens Rostery'}
            </p>
          </div>
        </div>
      </div>

      {/* ── What's been sent ── */}
      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Sent notifications</h3>
        {history === null ? <p className="text-sm text-slate-500">Loading…</p> : !history.length ? (
          <p className="text-sm text-slate-500">Nothing sent yet. Notifications you send appear here with how many phones got them.</p>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-3 py-2 font-medium text-slate-600 text-[12px] whitespace-nowrap">Sent</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-[12px]">Notification</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-[12px] whitespace-nowrap">To</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-[12px] whitespace-nowrap text-right">Phones reached</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((h) => (
                  <tr key={h.id} className="align-top">
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap tabular-nums">
                      {when(h.created_epoch)}
                      {h.sent_by_name && <div className="text-xs text-slate-500">by {h.sent_by_name}</div>}
                    </td>
                    <td className="px-3 py-2.5 min-w-[240px]">
                      <div className="flex items-center gap-1.5">
                        {h.kind === 'app_update' && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">App update</span>}
                        <span className="font-medium text-slate-900">{h.title}</span>
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{h.body}</div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">
                      {AUDIENCE_LABEL[h.audience] || h.audience}
                      <div className="text-xs text-slate-500">{plural(h.recipients, 'person', 'people')}{h.no_device ? `, ${h.no_device} without the app` : ''}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap tabular-nums">
                      <span className={clsx('inline-flex items-center gap-1', h.failed ? 'text-amber-700' : 'text-emerald-700')}>
                        {h.failed ? <AlertCircle size={13} /> : <CheckCircle2 size={13} />}
                        {h.accepted} of {h.devices}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
